import type { UnitCatalogResponse } from '@evesix/contracts';
import { DEFAULT_LAMP_CHANNELS, ROBOT_POSITIONS, WARDS } from '../domain/catalog';
import { formatLastCycle } from '../domain/format';
import type { CatalogSnapshot, Ward } from '../domain/types';
import type { ApiClient } from './api';

export interface CatalogService {
  load(): Promise<CatalogSnapshot>;
}

export const demoCatalog: CatalogService = {
  load: () =>
    Promise.resolve({
      wards: WARDS,
      sanitisedBeds: [],
      lampChannels: DEFAULT_LAMP_CHANNELS,
      activeCycle: null,
    }),
};

/** Wards and sanitised beds from the control API, so status survives reloads and spans units. */
export class HttpCatalogService implements CatalogService {
  readonly #client: ApiClient;
  readonly #unitId: string;
  readonly #now: () => number;

  constructor(client: ApiClient, unitId: string, now: () => number = Date.now) {
    this.#client = client;
    this.#unitId = unitId;
    this.#now = now;
  }

  async load(): Promise<CatalogSnapshot> {
    const catalog = await this.#client.get<UnitCatalogResponse>(
      `/units/${encodeURIComponent(this.#unitId)}`,
    );
    const now = this.#now();
    const wards: Ward[] = catalog.wards.map((w) => ({
      code: w.code,
      name: w.name,
      beds: w.beds,
      lastCycle: formatLastCycle(w.lastCycleAt, now),
    }));
    const active = catalog.activeCycle;
    return {
      wards,
      sanitisedBeds: catalog.sanitisedBeds,
      lampChannels: catalog.lampChannels,
      activeCycle: active
        ? {
            cycleId: active.cycleId,
            ward: wards.find((w) => w.code === active.wardCode) ?? {
              code: active.wardCode,
              name: active.wardCode,
              beds: Number(active.bed),
              lastCycle: '',
            },
            bed: active.bed,
            position: ROBOT_POSITIONS.find((p) => p.id === active.positionId) ?? {
              id: active.positionId,
              label: active.positionLabel,
              x: 50,
              y: 50,
            },
            durationSeconds: active.durationSeconds,
            wallClockSeconds: active.wallClockSeconds,
            channels: active.channels,
            startedAt: Date.parse(active.startedAt),
          }
        : null,
    };
  }
}
